import * as React from 'react'
import { ImageIcon } from 'lucide-react'
import { objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'
import { TableHead } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * The frame and the column around it. Both read design-system tokens rather than
 * Tailwind's scale — `--ds-thumb-width`/`-height` are Plan View's 56×42 (4:3) —
 * so the column moves when the tokens do.
 */
const FRAME = 'h-[var(--ds-thumb-height)] w-[var(--ds-thumb-width)]'
const COLUMN_WIDTH = 'w-[calc(var(--ds-thumb-width)+var(--ds-space-4))]'

/** A doorway usually sits above center in a venue photo, so an unfocused crop keeps the upper part. */
const UNFOCUSED_POSITION = '50% 40%'

/**
 * The header over a thumbnail column: announced, not drawn.
 *
 * Same reasoning as `TableActionsHead`. A word over a column of photographs
 * names the obvious, and being the widest thing in the column it would set the
 * column's width — so the label would push the pictures around.
 */
export function TableThumbHead({
  className,
  label = 'Photograph',
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { label?: string }): React.ReactElement {
  return (
    <TableHead className={cn(COLUMN_WIDTH, 'whitespace-nowrap pr-0', className)} {...props}>
      <span className="sr-only">{label}</span>
    </TableHead>
  )
}

interface Props {
  /** The record's own photograph, or null. */
  src: string | null
  /** Where the subject is, so the crop keeps it. */
  focalPoint?: FocalPoint | null | undefined
  /**
   * What to draw when there is no photograph, for records whose stand-in is a
   * drawing of their own — a furniture item's shape. Left out, the frame is the
   * empty well on the plan grid.
   */
  fallback?: React.ReactNode
  className?: string | undefined
}

/**
 * A row's photograph, as Plan View frames one: a small 4:3 picture with the chip
 * corner and an inner hairline (`media-ring`), so a pale photograph does not
 * bleed into the white row. Photographs are evidence, not wallpaper.
 *
 * No photograph is the sunken well on the plan grid with a quiet icon — never a
 * stock photograph, which cannot be told apart from the venue's own. It used to
 * be one: every event without a cover showed the same manor, which read as the
 * venue's building. Mirrors `.ds-td--thumb` / `.ds-thumb` in the design system.
 */
export function TableThumbCell({ src, focalPoint, fallback, className }: Props): React.ReactElement {
  return (
    <td className={cn('py-1.5 pl-4 pr-0', COLUMN_WIDTH, className)}>
      <span
        className={cn(
          'relative grid place-items-center overflow-hidden rounded-sm bg-surface-sunken text-ink-subtle',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-sm after:shadow-[var(--ds-media-ring)]',
          src === null && fallback == null ? 'plan-grid-fine' : undefined,
          FRAME,
        )}
      >
        {src !== null ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: objectPositionOf(focalPoint ?? null, UNFOCUSED_POSITION) }}
          />
        ) : (
          (fallback ?? <ImageIcon aria-hidden className="h-4 w-4" />)
        )}
      </span>
    </td>
  )
}
